import React, { useState } from 'react';
import { Menu } from 'lucide-react';

const BIOS = [
  {
    id: 'short',
    label: 'Short Bio',
    text: `Ryan Daniels is a mentalist and magician who combines impossible mind reading, modern magic, and sharp comedy to create interactive experiences built around the audience. From intimate close-up encounters to full-room stage performances, Ryan turns spectators into the stars of the show.`,
  },
  {
    id: 'medium',
    label: 'Medium Bio',
    text: `Ryan Daniels is a mentalist and magician known for combining impossible mind reading, modern magic, and sharp comedy with genuine audience interaction. Rather than simply performing for an audience, Ryan builds his performances around the people in the room—creating spontaneous, personal moments that leave guests laughing, amazed, and wondering how any of it was possible.

Based near Washington, DC and available nationwide, Ryan performs everything from close-up magic and mentalism during cocktail hours and receptions to interactive stage shows for corporate events, theaters, hotels, and special events.`,
  },
  {
    id: 'full',
    label: 'Full Bio',
    text: `Ryan Daniels is a mentalist and magician who believes the most memorable performances aren't just watched—they're experienced. Combining impossible mind reading, modern magic, sharp comedy, and genuine audience interaction, Ryan creates performances where the people in the room become an essential part of the show.

The result is entertainment that feels spontaneous, personal, and different every time. Thoughts are revealed, impossible predictions come true, and ordinary objects become part of moments that leave audiences laughing one second and asking, "How did he do that?" the next.

Ryan's performances are designed to fit the event around them. He can mingle among guests performing close-up magic and mentalism during cocktail hours, receptions, and networking events; take the stage for an interactive show designed for the entire room; or combine both into a full event entertainment experience.

Based near Washington, DC and available nationwide, Ryan brings his distinctive blend of mentalism, magic, and comedy to corporate events, theaters, hotels, and special events—with one goal: making the audience part of an experience they'll remember long after the event is over.`,
  },
  {
    id: 'emcee',
    label: 'Emcee Introduction',
    text: `Our next performer combines mind reading, magic, and comedy to create an experience where the audience becomes part of the show. Please put your hands together and welcome mentalist and magician, Ryan Daniels!`,
  },
];

const Booking = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openBio, setOpenBio] = useState(null);
  const [copiedBio, setCopiedBio] = useState(null);

  const handleCopy = async (id, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedBio(id);
      setTimeout(() => setCopiedBio(null), 2000);
    } catch (err) {
      // fallback
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopiedBio(id);
      setTimeout(() => setCopiedBio(null), 2000);
    }
  };

  return (
    <div
      className="min-h-screen text-white relative"
      style={{
        background:
          'linear-gradient(to bottom, #000000 0%, #0a0a0a 8%, #0f0f0f 15%, #0c0c0c 25%, #090909 40%, #060606 55%, #040404 70%, #020202 85%, #000000 100%)',
      }}
    >
      {/* Subtle pink glows */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <div className="absolute top-[10%] -left-64 w-[800px] h-[1200px] bg-magenta/[0.02] rounded-full blur-[200px]"></div>
        <div className="absolute top-[35%] -right-64 w-[800px] h-[1200px] bg-magenta/[0.02] rounded-full blur-[200px]"></div>
        <div className="absolute top-[65%] -left-64 w-[700px] h-[1000px] bg-pink-500/[0.015] rounded-full blur-[200px]"></div>
      </div>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-lg border-b border-white/5 bg-black/80">
        <div className="max-w-7xl mx-auto px-6 py-0">
          <div className="flex items-center justify-between">
            <a href="/" aria-label="Home" className="cursor-pointer" data-testid="booking-logo-home">
              <img
                src="https://customer-assets.emergentagent.com/job_2307e085-4eaf-4338-88bf-6170046d3d85/artifacts/mjuj40fa_Ryan%20daniels%20mentalist-4.png"
                alt="Ryan Daniels Mentalist"
                style={{ height: '140px', marginTop: '-8px', marginBottom: '-8px' }}
              />
            </a>
            <div className="hidden md:flex items-center gap-8">
              <a href="/" className="nav-link">Home</a>
              <a href="/corporate" className="nav-link">Corporate</a>
              <a href="/booking" className="nav-link">Booking</a>
              <a href="/#contact" className="cta-button">Book Now</a>
            </div>
            <button
              className="md:hidden text-white p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Menu"
              data-testid="booking-mobile-menu-btn"
            >
              <Menu size={28} />
            </button>
          </div>
          {mobileMenuOpen && (
            <div className="md:hidden pb-4 flex flex-col gap-3">
              <a href="/" className="nav-link text-left py-2">Home</a>
              <a href="/corporate" className="nav-link text-left py-2">Corporate</a>
              <a href="/booking" className="nav-link text-left py-2">Booking</a>
              <a href="/#contact" className="cta-button w-full text-center">Book Now</a>
            </div>
          )}
        </div>
      </nav>

      {/* Hero */}
      <section
        className="relative z-10 pt-40 md:pt-48 pb-16 md:pb-24 px-6"
        data-testid="booking-hero"
      >
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xs md:text-sm font-semibold tracking-[0.3em] text-magenta uppercase mb-1">
            Ryan Daniels
          </p>
          <p className="text-sm md:text-base text-gray-400 italic mb-8">
            Mentalist &amp; Magician
          </p>
          <h1
            className="font-bold leading-tight tracking-tight mb-6 text-[clamp(2rem,7vw,5rem)]"
            style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #ff1f8f 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            BOOKING RESOURCES
          </h1>
          <div className="section-divider"></div>
          <p className="mt-8 md:mt-10 text-base md:text-lg text-gray-300 leading-relaxed max-w-2xl mx-auto">
            Performance footage, downloadable materials, promotional assets, and booking information for event planners, agencies, venues, and entertainment professionals.
          </p>
        </div>
      </section>

      {/* Performance Footage */}
      <section className="py-14 md:py-16 px-6 relative z-10" data-testid="booking-performance-footage">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="section-heading mb-4">Performance Footage</h2>
            <div className="section-divider"></div>
          </div>
          <div className="space-y-3">
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/static/promo_reel.mp4?download=true`}
              download="Ryan-Daniels-Promo-Reel.mp4"
              className="flex items-center justify-between border border-white/10 hover:border-magenta rounded-2xl bg-neutral-mid/30 hover:bg-neutral-mid/50 px-6 py-5 transition-colors group"
              data-testid="booking-promo-reel-download"
            >
              <div>
                <p className="text-white font-semibold text-sm md:text-base tracking-wide">Promo Reel</p>
                <p className="text-gray-500 text-xs mt-1">Download MP4</p>
              </div>
              <span className="text-magenta group-hover:translate-x-1 transition-transform text-xl">↓</span>
            </a>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/static/short_performance.mp4?download=true`}
              download="Ryan-Daniels-Short-Performance.mp4"
              className="flex items-center justify-between border border-white/10 hover:border-magenta rounded-2xl bg-neutral-mid/30 hover:bg-neutral-mid/50 px-6 py-5 transition-colors group"
              data-testid="booking-short-performance-download"
            >
              <div>
                <p className="text-white font-semibold text-sm md:text-base tracking-wide">Short Stage Performance</p>
                <p className="text-gray-500 text-xs mt-1">Download MP4 · 5 minutes</p>
              </div>
              <span className="text-magenta group-hover:translate-x-1 transition-transform text-xl">↓</span>
            </a>
            <a
              href="https://youtu.be/625-6SWDqlU"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between border border-white/10 hover:border-magenta rounded-2xl bg-neutral-mid/30 hover:bg-neutral-mid/50 px-6 py-5 transition-colors group"
              data-testid="booking-full-show-link"
            >
              <div>
                <p className="text-white font-semibold text-sm md:text-base tracking-wide">Full Stage Performance</p>
                <p className="text-gray-500 text-xs mt-1">Watch on YouTube · 20 minutes</p>
              </div>
              <span className="text-magenta group-hover:translate-x-1 transition-transform text-xl">→</span>
            </a>
          </div>
        </div>
      </section>

      {/* Downloadable Materials */}
      <section className="py-14 md:py-16 px-6 relative z-10" data-testid="booking-downloadable-materials">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="section-heading mb-4">Downloadable Materials</h2>
            <div className="section-divider"></div>
          </div>
          <div className="space-y-3">
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/static/ryan_daniels_one_sheet.pdf?download=true`}
              download="Ryan Daniels One Sheet.pdf"
              className="flex items-center justify-between border border-white/10 hover:border-magenta rounded-2xl bg-neutral-mid/30 hover:bg-neutral-mid/50 px-6 py-5 transition-colors group"
              data-testid="booking-one-sheet-download"
            >
              <div>
                <p className="text-white font-semibold text-sm md:text-base tracking-wide">Ryan Daniels One Sheet</p>
                <p className="text-gray-500 text-xs mt-1">Download PDF</p>
              </div>
              <span className="text-magenta group-hover:translate-x-1 transition-transform text-xl">↓</span>
            </a>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/static/ryan_daniels_pitch_deck.pdf?download=true`}
              download="Ryan Daniels Pitch Deck.pdf"
              className="flex items-center justify-between border border-white/10 hover:border-magenta rounded-2xl bg-neutral-mid/30 hover:bg-neutral-mid/50 px-6 py-5 transition-colors group"
              data-testid="booking-pitch-deck-download"
            >
              <div>
                <p className="text-white font-semibold text-sm md:text-base tracking-wide">Ryan Daniels Pitch Deck</p>
                <p className="text-gray-500 text-xs mt-1">Download PDF</p>
              </div>
              <span className="text-magenta group-hover:translate-x-1 transition-transform text-xl">↓</span>
            </a>
          </div>
        </div>
      </section>

      {/* Photos & Promotional Materials */}
      <section className="py-14 md:py-16 px-6 relative z-10" data-testid="booking-photos">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="section-heading mb-4">Photos &amp; Promotional Materials</h2>
            <div className="section-divider"></div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-5">
            {[
              { file: "hand_reach.webp", title: "Hand Reach", filename: "Ryan-Daniels-Hand-Reach.webp" },
              { file: "head_shot.webp", title: "Head Shot", filename: "Ryan-Daniels-Head-Shot.webp" },
              { file: "stage_performance.webp", title: "Stage Performance", filename: "Ryan-Daniels-Stage-Performance.webp" },
              { file: "full_stage.jpg", title: "Full Stage", filename: "Ryan-Daniels-Full-Stage.jpg" },
              { file: "spectator_shot.webp", title: "Spectator Shot", filename: "Ryan-Daniels-Spectator-Shot.webp" },
              { file: "close_up_performance.jpg", title: "Close-Up Performance", filename: "Ryan-Daniels-Close-Up-Performance.jpg" },
            ].map((photo) => (
              <a
                key={photo.file}
                href={`${process.env.REACT_APP_BACKEND_URL}/api/static/${photo.file}?download=true`}
                download={photo.filename}
                className="group block border border-white/10 hover:border-magenta rounded-2xl overflow-hidden bg-neutral-mid/30 hover:bg-neutral-mid/50 transition-colors"
                data-testid={`booking-photo-${photo.file.split('.')[0].replace(/_/g, '-')}-download`}
              >
                <div className="aspect-square overflow-hidden bg-black">
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/static/${photo.file}`}
                    alt={photo.title}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="flex items-center justify-between px-4 py-3 md:px-5 md:py-4">
                  <div>
                    <p className="text-white font-semibold text-xs md:text-sm tracking-wide">{photo.title}</p>
                    <p className="text-gray-500 text-[10px] md:text-xs mt-0.5">Download</p>
                  </div>
                  <span className="text-magenta group-hover:translate-x-1 transition-transform text-lg md:text-xl">↓</span>
                </div>
              </a>
            ))}
          </div>

          {/* Bios */}
          <div className="mt-14 md:mt-16" data-testid="booking-bios">
            <div className="text-center mb-8">
              <h3 className="text-white text-2xl md:text-3xl font-semibold tracking-tight">Bios</h3>
            </div>
            <div className="space-y-3 max-w-3xl mx-auto">
              {BIOS.map((bio) => {
                const isOpen = openBio === bio.id;
                return (
                  <div
                    key={bio.id}
                    className={`border rounded-2xl bg-neutral-mid/30 transition-colors ${
                      isOpen ? 'border-magenta' : 'border-white/10 hover:border-magenta'
                    }`}
                    data-testid={`booking-bio-${bio.id}`}
                  >
                    <button
                      type="button"
                      onClick={() => setOpenBio(isOpen ? null : bio.id)}
                      className="w-full flex items-center justify-between px-6 py-5 text-left hover:bg-neutral-mid/50 rounded-2xl transition-colors"
                      aria-expanded={isOpen}
                      data-testid={`booking-bio-${bio.id}-toggle`}
                    >
                      <span className="text-white font-semibold text-sm md:text-base tracking-wide uppercase">
                        {bio.label}
                      </span>
                      <span
                        className={`text-magenta text-xl transition-transform duration-300 ${
                          isOpen ? 'rotate-45' : ''
                        }`}
                      >
                        +
                      </span>
                    </button>
                    {isOpen && (
                      <div className="px-6 pb-6 pt-1">
                        <div className="border-t border-white/10 pt-5">
                          <div
                            className="text-gray-300 text-sm md:text-base leading-relaxed whitespace-pre-line"
                            data-testid={`booking-bio-${bio.id}-text`}
                          >
                            {bio.text}
                          </div>
                          <div className="mt-5">
                            <button
                              type="button"
                              onClick={() => handleCopy(bio.id, bio.text)}
                              className="inline-flex items-center gap-2 border border-magenta/60 hover:border-magenta bg-magenta/10 hover:bg-magenta/20 text-magenta hover:text-white text-xs md:text-sm font-semibold tracking-wider uppercase px-5 py-2.5 rounded-full transition-colors"
                              data-testid={`booking-bio-${bio.id}-copy`}
                            >
                              {copiedBio === bio.id ? 'Copied!' : 'Copy Bio'}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <p className="text-gray-500 text-xs md:text-sm italic text-center mt-6 max-w-3xl mx-auto">
              These bios may be used for event programs, websites, press materials, and promotional purposes.
            </p>
          </div>
        </div>
      </section>

      {/* Bring the Impossible to Your Event */}
      <section className="py-20 md:py-28 px-6 relative z-10" data-testid="booking-contact">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="section-heading mb-4">Bring the Impossible to Your Event</h2>
          <div className="section-divider"></div>
          <div className="mt-7 md:mt-8">
            <a
              href="mailto:ryan@ryandanielsmentalist.com"
              className="inline-block text-lg md:text-2xl font-medium text-magenta hover:text-magenta/80 transition-colors underline underline-offset-8"
              data-testid="booking-email-link"
            >
              ryan@ryandanielsmentalist.com
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/10 py-6 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <p className="body-small text-gray-500">
            &copy; 2026 Ryan Daniels. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Booking;
